import { useState, useEffect, useCallback, useRef } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, ShieldCheck, Upload, Trash2, ImageIcon } from "lucide-react";

const SECTIONS = [
  { key: "mission", label: "Mission", area: true },
  { key: "vision", label: "Vision", area: true },
  { key: "brand_promise", label: "Brand promise", area: true },
  { key: "audience", label: "Audience", area: true },
  { key: "brand_voice", label: "Brand voice", area: true },
  { key: "writing_framework", label: "Writing framework", area: true, rows: "min-h-[190px]" },
  { key: "words_to_use", label: "Words to use", area: true },
  { key: "words_to_avoid", label: "Words to avoid", area: true },
  { key: "brand_colors", label: "Brand colors", area: true },
  { key: "fonts", label: "Fonts", area: false },
  { key: "image_style", label: "Image style", area: true },
  { key: "content_standards", label: "Content standards", area: true, rows: "min-h-[170px]" },
];

const LOGO_SLOTS = [
  { variant: "primary", label: "Primary logo" },
  { variant: "light", label: "Light version (optional)" },
  { variant: "dark", label: "Dark version (optional)" },
];

const FILTER_QUESTIONS = [
  "Is this clear and easy to understand?",
  "Is this genuinely useful to the audience?",
  "Does this sound like the brand?",
  "Would the reader be glad they spent time on this?",
];

export default function BrandCenter() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [logoTs, setLogoTs] = useState(Date.now());
  const fileRefs = { primary: useRef(null), light: useRef(null), dark: useRef(null) };

  const load = useCallback(async () => {
    try { const r = await api.get("/brand"); setData(r.data); }
    catch (e) { toast.error("Could not load Brand Center."); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/brand", data); toast.success("Brand standards saved."); }
    catch (e) { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (variant, input) => {
    const file = input.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("variant", variant);
    setUploading(variant);
    try {
      await api.post("/brand/logo", fd);
      await load();
      setLogoTs(Date.now());
      toast.success("Logo uploaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload the logo.");
    } finally {
      setUploading(null);
      if (input) input.value = "";
    }
  };

  const removeLogo = async (variant) => {
    try {
      await api.delete(`/brand/logo/${variant}`);
      await load();
      setLogoTs(Date.now());
      toast.success("Logo removed.");
    } catch (e) { toast.error("Could not remove the logo."); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div>
      <PageHeader
        testid="brand-header"
        title="Brand Center"
        subtitle="Your voice, your promise, your standards, the heart of your brand."
        action={
          <Button onClick={save} disabled={saving} data-testid="brand-save-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </Button>
        }
      />

      {/* Permanent content filter */}
      <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6 bg-secondary text-secondary-foreground" data-testid="content-filter">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="h-5 w-5" />
          <h2 className="font-heading text-xl">Content filter</h2>
        </div>
        <p className="text-secondary-foreground/80 text-sm mb-4">Before anything goes out, it must pass through these four gentle questions:</p>
        <ul className="grid sm:grid-cols-2 gap-3">
          {FILTER_QUESTIONS.map((qn) => (
            <li key={qn} className="flex items-start gap-2 text-sm">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-secondary-foreground/60 shrink-0" />
              <span>{qn}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Brand logo upload */}
      <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6" data-testid="brand-logo-card">
        <div className="flex items-center gap-2 mb-1">
          <ImageIcon className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-xl">Brand logo</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Upload your logo so it stays with your workspace. PNG, JPG, WEBP, GIF or SVG, up to 8&nbsp;MB.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {LOGO_SLOTS.map(({ variant, label }) => {
            const meta = data[`logo_${variant}`];
            const busy = uploading === variant;
            return (
              <div key={variant} className="rounded-2xl border border-border p-4 flex flex-col" data-testid={`brand-logo-slot-${variant}`}>
                <Label className="text-sm mb-3">{label}</Label>
                <div className="flex-1 rounded-xl bg-muted/40 border border-border/60 flex items-center justify-center overflow-hidden mb-3" style={{ minHeight: "120px" }}>
                  {meta?.storage_path ? (
                    <img
                      src={`${API}/brand/logo/${variant}?v=${logoTs}`}
                      alt={`${label} preview`}
                      data-testid={`brand-logo-preview-${variant}`}
                      className="max-h-[120px] max-w-full object-contain p-2"
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">No logo yet</span>
                  )}
                </div>
                <input
                  ref={fileRefs[variant]}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  className="hidden"
                  data-testid={`brand-logo-input-${variant}`}
                  onChange={(e) => uploadLogo(variant, e.target)}
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => fileRefs[variant].current?.click()}
                    data-testid={`brand-logo-upload-${variant}`}
                    className="rounded-full gap-1 flex-1"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {meta?.storage_path ? "Replace" : "Upload"}
                  </Button>
                  {meta?.storage_path && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeLogo(variant)}
                      data-testid={`brand-logo-remove-${variant}`} className="rounded-full text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-5">
          <Label className="text-sm mb-2 block">Logo notes / links <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input data-testid="brand-logo_files" value={data.logo_files || ""} onChange={(e) => set("logo_files", e.target.value)}
            placeholder="e.g. link to a shared drive, usage notes, file names…" className="rounded-xl" />
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-5" data-testid="brand-sections">
        {SECTIONS.map((s) => (
          <Card key={s.key} className={`rounded-3xl border-border shadow-soft p-6 ${s.rows ? "md:col-span-2" : ""}`}>
            <Label className="font-heading text-lg mb-3 block">{s.label}</Label>
            {s.area ? (
              <Textarea data-testid={`brand-${s.key}`} value={data[s.key] || ""} onChange={(e) => set(s.key, e.target.value)}
                className={`rounded-xl ${s.rows || "min-h-[90px]"}`} />
            ) : (
              <Input data-testid={`brand-${s.key}`} value={data[s.key] || ""} onChange={(e) => set(s.key, e.target.value)} className="rounded-xl" />
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
